import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AuthorizationService } from './authorization.service';
import { NotificationEvent, NotificationResponse } from '../types/notification.types';
import { BehaviorSubject, fromEvent, Observable, tap } from 'rxjs';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { MessageService } from 'primeng/api';
import { Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private http = inject(HttpClient);
  private authService = inject(AuthorizationService);
  private messageService = inject(MessageService);
  private titleService = inject(Title);

  private readonly baseUrl = environment.api.paramsUrl + '/notifications';
  private readonly wsUrl = environment.api.wsUrl;
  private readonly APP_TITLE = 'SIGA-SIGMAP';

  private stompClient: Client | null = null;

  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubject.asObservable();

  private notificationReceivedSubject = new BehaviorSubject<NotificationResponse | null>(null);
  notificationReceived$ = this.notificationReceivedSubject.asObservable();

  private titleInterval: any;
  private isOriginalTitle = true;

  constructor() {
    this.init();
    this.setupWindowListeners();
  }

  private setupWindowListeners() {
    fromEvent(window, 'focus').subscribe(() => {
      this.resetTitle();
    });
  }

  private resetTitle() {
    if (this.titleInterval) {
      clearInterval(this.titleInterval);
      this.titleInterval = null;
    }
    this.isOriginalTitle = true;
    this.titleService.setTitle(this.APP_TITLE);
  }

  private notifyTitle() {
    if (document.hidden && !this.titleInterval) {
      this.titleInterval = setInterval(() => {
        if (this.isOriginalTitle) {
          this.titleService.setTitle(`🔴 Nouveau message - ${this.APP_TITLE}`);
        } else {
          this.titleService.setTitle(this.APP_TITLE);
        }
        this.isOriginalTitle = !this.isOriginalTitle;
      }, 2000);
    }
  }

  init() {
    this.authService.isReady$.subscribe(ready => {
      if (ready && this.authService.isAuthenticated()) {
        this.connectWebSocket();
        this.updateUnreadCount();
      } else {
        this.disconnectWebSocket();
      }
    });
  }

  private connectWebSocket() {
    if (this.stompClient && this.stompClient.active) {
      return;
    }

    const token = this.authService.keycloak.token;
    if (!token) return;

    this.stompClient = new Client({
      webSocketFactory: () => new SockJS(this.wsUrl),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    this.stompClient.onConnect = () => {
      this.stompClient?.subscribe('/user/queue/notifications', (message: IMessage) => {
        this.handleWebSocketMessage(JSON.parse(message.body));
      });
    };

    this.stompClient.onStompError = frame => {
      console.error('STOMP error', frame.headers['message']);
    };

    this.stompClient.activate();
  }

  private disconnectWebSocket() {
    if (this.stompClient) {
      this.stompClient.deactivate();
      this.stompClient = null;
    }
  }

  private handleWebSocketMessage(event: NotificationEvent) {
    if (event.unreadCount !== undefined) {
      this.unreadCountSubject.next(event.unreadCount);
    }

    if (event.eventType === 'NEW' && event.notification) {
      this.notificationReceivedSubject.next(event.notification);
      this.showToast(event.notification);
      this.notifyTitle();
    }
  }

  private showToast(notification: NotificationResponse) {
    this.messageService.add({
      severity: this.getSeverity(notification.type),
      summary: notification.title,
      detail: notification.message,
      life: 5000,
      data: notification,
    });
  }

  private getSeverity(type: string): string {
    switch (type) {
      case 'SUCCESS':
        return 'success';
      case 'WARNING':
        return 'warn';
      case 'ERROR':
        return 'error';
      default:
        return 'info';
    }
  }

  // REST API Methods

  getNotifications(page = 0, size = 20): Observable<any> {
    return this.http.get(`${this.baseUrl}?page=${page}&size=${size}`);
  }

  updateUnreadCount() {
    this.http.get<{ count: number }>(`${this.baseUrl}/unread-count`).subscribe(res => {
      this.unreadCountSubject.next(res.count);
    });
  }

  markAsRead(id: number): Observable<NotificationResponse> {
    return this.http.put<NotificationResponse>(`${this.baseUrl}/${id}/read`, {}).pipe(
      tap(() => {
        this.updateUnreadCount();
        this.resetTitle();
      })
    );
  }

  markAllAsRead(): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/read-all`, {}).pipe(
      tap(() => {
        this.unreadCountSubject.next(0);
        this.resetTitle();
      })
    );
  }

  deleteNotification(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(tap(() => this.updateUnreadCount()));
  }
}
